import React, { useMemo } from 'react';

import {
  Box,
  Divider,
  Paper,
} from '@mui/material';
import { usePermissions } from 'react-admin';

import View from './View';
import Create from './Create';
import VersionSelect from './VersionSelect';
import Reset from './Reset';
import Publish from './Publish';
import Type from './Type';

import ruleTypes, { RuleTypes } from '../../../../constants/ruleTypes';
import { RuleViews } from '../../../../constants/ruleView';
import Statistics from './Statistics';
import Process from './Process';
import GroupsToInclude from './GroupsToInclude';
import Release from './Release';
import Deactivate from './Deactivate';
import ReviewHistory from './ReviewHistory';
import VersionHeading from './VersionHeading';
import actions from '../../../../constants/actions';
import hasAccess, { getMatchingPermissions } from '../../../../utilities/hasAccess';
import { Permission } from '../../../../utilities/Authprovider';
import { RuleVersionRecord, RuleVersionStatus } from '../../../../hooks/useRuleVersion';

type RuleActionsInput = {
  ruleType: RuleTypes;
  changeRuleType: (x: RuleTypes) => void;
  view: RuleViews;
  changeView: (x: RuleViews) => void;
  version: string;
  versionRecord?: RuleVersionRecord;
  versionStatus?: RuleVersionStatus;
  latestLiveVersionId?: string;
  isDraft: boolean;
  isVersionLoading: boolean;
  changeVersion: (x: string) => void;
  resetToLatestVersion: () => void;
  selectLatestVersion: (isTestVersion: boolean) => void;
  refetchVersion: () => void;
}

const RuleActions = ({
  ruleType,
  changeRuleType,
  view,
  changeView,
  version,
  versionRecord,
  versionStatus,
  latestLiveVersionId,
  isDraft,
  isVersionLoading,
  changeVersion,
  resetToLatestVersion,
  selectLatestVersion,
  refetchVersion,
}: RuleActionsInput) => {
  const { permissions } = usePermissions<Permission[]>();

  const showReviewHistory = useMemo(() => {
    const publishPermissions = getMatchingPermissions(permissions, actions.RULE_VERSION_PUBLISH);
    const releasePermissions = getMatchingPermissions(permissions, actions.RULE_VERSION_RELEASE);

    const publishRequiresReview = publishPermissions.some(
      (p) => typeof p.attributes === 'object'
        && p.attributes !== null
        && (p.attributes as { requirePeerReview?: boolean }).requirePeerReview === true,
    );
    const releaseRequiresReview = releasePermissions.some(
      (p) => typeof p.attributes === 'object'
        && p.attributes !== null
        && (p.attributes as { requirePeerReview?: boolean }).requirePeerReview === true,
    );

    return publishRequiresReview || releaseRequiresReview;
  }, [permissions]);

  // Reset, Publish and Create only apply to the Draft, so they are hidden on a published version.
  // While the version is loading it is not known yet whether the Draft will be shown.
  const showDraftActions = isDraft && !isVersionLoading;

  return (
    <Box
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      columnGap={3}
      rowGap={2}
      width="100%"
    >
      <VersionHeading
        status={versionStatus}
        number={versionRecord?.number}
        isLoading={isVersionLoading}
        openDraft={() => changeVersion('draft')}
        openLiveVersion={latestLiveVersionId
          ? () => changeVersion(latestLiveVersionId)
          : undefined}
      />
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          flexWrap: 'wrap',
          marginLeft: 'auto',
          gap: 0.5,
          padding: 0.5,
        }}
      >
        {hasAccess(permissions, actions.RULE_VERSION_LIST)
          && <VersionSelect ruleType={ruleType} changeVersion={changeVersion} version={version} />}
        {showDraftActions && hasAccess(permissions, actions.RULE_UPDATE)
          && <Reset ruleType={ruleType} />}
        {showDraftActions && hasAccess(permissions, actions.RULE_VERSION_PUBLISH) && (
          <Publish ruleType={ruleType} onPublished={selectLatestVersion} />
        )}
        {hasAccess(permissions, actions.RULE_VERSION_RELEASE) && (
          <Release
            disabled={isDraft || isVersionLoading}
            version={version}
            onReleased={() => selectLatestVersion(false)}
          />
        )}
        <Deactivate
          disabled={isDraft || isVersionLoading}
          version={version}
          onDeactivated={refetchVersion}
        />
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
        <Type
          resetToLatestVersion={resetToLatestVersion}
          ruleType={ruleType}
          changeRuleType={changeRuleType}
        />
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
        <View view={view} changeView={changeView} />
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1, display: ruleType === ruleTypes.REQUEST ? 'flex' : 'none' }} />
        { ruleType === ruleTypes.REQUEST && <Process />}
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
        <GroupsToInclude />
        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
        {showDraftActions && hasAccess(permissions, actions.RULE_CREATE)
          && <Create ruleType={ruleType} view={view} />}
        {showReviewHistory && <ReviewHistory isDraft={isDraft} ruleType={ruleType} view={view} />}
        <Statistics />
      </Paper>
    </Box>
  );
};

export default RuleActions;
